<template>
    <b-modal
        v-if="group !== null"
        size="lg"
        v-model="modalShow"
        :title="`${groupTypeName}追加`"
        ok-title="追加"
        ok-variant="yokumiru-orange"
        cancel-title="キャンセル"
        cancel-variant="yokumiru-gray"
        :ok-disabled="inProgress || !telObj.valid"
        :cancel-disabled="inProgress"
        @ok="handleOk"
        @cancel="close"
        @hidden="close">
        <form @submit.stop.prevent="addGroup">
            <div class="modalSection">
                <h5 class="modalHeading">基本情報</h5>
                <div class="row">
                    <div class="col-12 col-lg-6">
                        <div class="form-group m-form__group">
                            <label for="name" class="is-required-ja">{{ groupTypeName }}名</label>
                            <input
                                v-model="group.name"
                                id="name"
                                class="form-control m-input"
                                :class="{ 'is-invalid' : errors.name }"
                                type="text"
                                :placeholder="`${groupTypeName}名を入力`">
                            <div
                                v-if="errors.name"
                                class="invalid-feedback d-block mt-1">
                                {{ errors.name[0] }}
                            </div>
                        </div>
                    </div>

                    <div class="col-12 col-lg-6">
                        <div class="form-group m-form__group">
                            <label for="name_kana">{{ groupTypeName }}名（フリガナ）</label>
                            <input
                                v-model="group.name_kana"
                                id="name_kana"
                                class="form-control m-input"
                                :class="{ 'is-invalid' : errors.name_kana }"
                                type="text"
                                :placeholder="`${groupTypeName}名（フリガナ）を入力`">
                            <div
                                v-if="errors.name_kana"
                                class="invalid-feedback d-block mt-1">
                                {{ errors.name_kana[0] }}
                            </div>
                        </div>
                    </div>
                </div>

                <div>
                    <div class="row">
                        <div class="col-12 col-lg-6">
                            <div class="form-group m-form__group">
                                <label for="address">住所</label>
                                <input
                                    v-model="group.address"
                                    id="address"
                                    class="form-control m-input"
                                    :class="{ 'is-invalid' : errors.address }"
                                    type="text"
                                    placeholder="住所を入力">
                                <div
                                    v-if="errors.address"
                                    class="invalid-feedback d-block mt-1">
                                    {{ errors.address[0] }}
                                </div>
                            </div>
                        </div>

                        <div class="col-12 col-lg-6">
                            <div class="form-group m-form__group">
                                <label for="tel">
                                    電話番号<span class="small">（滞在している国の国際番号）</span>
                                </label>
                                <vue-tel-input
                                    v-model="group.tel"
                                    id="tel"
                                    name="tel"
                                    :class="{ 'is-invalid' : errors.tel || telObj.valid === false }"
                                    placeholder="000-1111-2222"
                                    valid-characters-only
                                    default-country="JP"
                                    @validate="onValidateTel">
                                </vue-tel-input>
                                <div
                                    v-if="errors.tel"
                                    class="invalid-feedback d-block">
                                    {{ errors.tel[0] }}
                                </div>
                                <div
                                    v-if="telObj.valid === false"
                                    class="invalid-feedback d-block">
                                    電話番号を正しく入力してください
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 col-lg-6">
                        <div class="form-group m-form__group">
                            <label for="name">代表者名</label>
                            <input
                                v-model="group.representative"
                                id="representative"
                                class="form-control m-input"
                                :class="{ 'is-invalid' : errors.representative }"
                                type="text"
                                placeholder="代表者名を入力">
                            <div
                                v-if="errors.representative"
                                class="invalid-feedback d-block mt-1">
                                {{ errors.representative[0] }}
                            </div>
                        </div>
                    </div>

                    <div class="col-12 col-lg-6">
                        <div class="form-group m-form__group">
                            <label for="name_kana">代表者名（フリガナ）</label>
                            <input
                                v-model="group.representative_kana"
                                id="representative_kana"
                                class="form-control m-input"
                                :class="{ 'is-invalid' : errors.representative_kana }"
                                type="text"
                                placeholder="代表者名（フリガナ）を入力">
                            <div
                                v-if="errors.representative_kana"
                                class="invalid-feedback d-block mt-1">
                                {{ errors.representative_kana[0] }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <div class="form-group m-form__group">
                            <label for="memo">メモ</label>
                            <textarea
                                v-model="group.memo"
                                id="memo"
                                rows="3"
                                class="form-control"
                                :placeholder="`メモを入力（${$locale.patient}側には表示されません）`"/>
                            <div
                                v-if="errors.memo"
                                class="invalid-feedback d-block mt-1">
                                {{ errors.memo[0] }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col">
                        <div class="form-group m-form__group">
                            <label>管理用タグ</label>
                            <vue-tags-input
                                v-model="groupManagementTag"
                                :tags="groupManagementTags"
                                @tags-changed="tags => syncGroupManagementTag(tags)"
                                placeholder="管理用タグを追加"
                            />
                            <div v-if="errors.group_management_tags"
                                 class="invalid-feedback d-block">
                                {{ errors.group_management_tags[0] }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modalSection">
                <h5 class="modalHeading">担当者設定</h5>
                <div class="row">
                    <div class="col-12">
                        <div class="form-group m-form__group">
                            <label for="is_tab_consultation">{{ `${$locale.consultation_host}一覧タブ` }}</label>
                            <div>
                                <div class="form-check form-check-inline">
                                    <input
                                        v-model="group.is_tab_consultation"
                                        type="radio"
                                        id="is_tab_consultation_true"
                                        class="form-check-input"
                                        :value="true">
                                    <label
                                        class="form-check-label pr-3"
                                        for="is_tab_consultation_true">
                                        表示する
                                    </label>
                                    <input
                                        v-model="group.is_tab_consultation"
                                        type="radio"
                                        id="is_tab_consultation_false"
                                        class="form-check-input"
                                        :value="false">
                                    <label
                                        class="form-check-label"
                                        for="is_tab_consultation_false">
                                        表示しない
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-12">
                        <div class="form-group m-form__group">
                            <label>{{ `${$locale.consultation_host}詳細` }}</label>
                            <div>
                                <div class="form-check form-check-inline">
                                    <input
                                        v-model="group.is_viewable_authorizer"
                                        type="radio"
                                        id="is_viewable_authorizer_true"
                                        class="form-check-input"
                                        :value="true"
                                        :disabled="!group.is_tab_consultation || isOrganization">
                                    <label
                                        class="form-check-label pr-3"
                                        for="is_viewable_authorizer_true">
                                        表示する
                                    </label>
                                    <input
                                        v-model="group.is_viewable_authorizer"
                                        type="radio"
                                        id="is_viewable_authorizer_false"
                                        class="form-check-input"
                                        :value="false"
                                        :disabled="!group.is_tab_consultation || isOrganization">
                                    <label
                                        class="form-check-label"
                                        for="is_viewable_authorizer_false">
                                        表示しない
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="isOrganization"
                    class="row">
                    <div class="col-12">
                        <div class="form-group m-form__group">
                            <label for="memo">利用アカウント発行時のメッセージ</label>
                            <quill-editor
                                ref="myQuillEditor"
                                v-model="group.invitation_message"
                                :options="editorOption">
                            </quill-editor>
                            <div
                                v-if="errors.invitation_message"
                                class="invalid-feedback d-block mt-1">
                                {{ errors.invitation_message[0] }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modalSection">
                <h5 class="modalHeading">サービス設定</h5>
                <div class="row">
                    <div class="col">
                        <div class="form-group m-form__group">
                            <label>利用サービスモデル</label>
                            <div v-for="(id, index) in groupPlanIds">
                                <div class="d-flex align-items-center mb-2">
                                    <select
                                        v-model="groupPlanIds[index]"
                                        @change="updateGroupPlanIdsList"
                                        :disabled="inProgress || id === defaultPlanId"
                                        :id="`group_plan_id_${index}`"
                                        class="form-control"
                                        :class="{ 'is-invalid' : errors.plan_ids }"
                                        style="max-width: 380px;">
                                        <template v-if="defaultPlanId !== null && index < 1">
                                            <option
                                                :key="`plan_default_${index}`"
                                                :value="defaultPlanId">
                                                {{ defaultPlan.name }}
                                            </option>
                                        </template>
                                        <template v-else>
                                            <option :value="null">---</option>
                                            <option
                                                v-for="plan in filteredPlans"
                                                :key="`plan_${plan.id}_${index}`"
                                                :value="plan.id">
                                                {{ plan.name }}
                                            </option>
                                        </template>
                                    </select>
                                    <div
                                        v-if="id !== defaultPlanId && groupPlanIds.length > 1 && groupPlanIds.length !== index + 1"
                                        @click="onClickRemoveSelectPlan(index)"
                                        class="incDecButton incDecButtonRemove">
                                        -
                                    </div>
                                    <div
                                        v-if="groupPlanIds.length === index + 1"
                                        @click="onClickAddSelectPlan"
                                        class="incDecButton">
                                        +
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="errors.plan_ids"
                                class="invalid-feedback d-block mt-1">
                                {{ errors.plan_ids[0] }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 col-lg-6">
                        <div class="form-group m-form__group">
                            <label>健康管理パッケージ</label>
                            <div>
                                <div class="form-check form-check-inline">
                                    <input
                                        v-model="group.is_health_package"
                                        type="radio"
                                        id="is_health_package_true"
                                        class="form-check-input"
                                        :value="true"
                                        :disabled="isOrganization">
                                    <label
                                        class="form-check-label pr-3"
                                        for="is_health_package_true">
                                        利用する
                                    </label>
                                    <input
                                        v-model="group.is_health_package"
                                        type="radio"
                                        id="is_health_package_false"
                                        class="form-check-input"
                                        :value="false"
                                        :disabled="isOrganization">
                                    <label
                                        class="form-check-label"
                                        for="is_health_package_false">
                                        利用しない
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modalSection">
                <h5 class="modalHeading">メンタルチェック設定</h5>
                <div class="row">
                    <div class="col">
                        <div class="form-group m-form__group">
                            <label>メンタルチェック</label>
                            <div>
                                <div class="form-check form-check-inline">
                                    <input
                                        v-model="group.is_health_survey"
                                        type="radio"
                                        id="is_health_survey_true"
                                        class="form-check-input"
                                        :value="true"
                                        :disabled="isOrganization">
                                    <label
                                        class="form-check-label pr-3"
                                        for="is_health_survey_true">
                                        利用する
                                    </label>
                                    <input
                                        v-model="group.is_health_survey"
                                        type="radio"
                                        id="is_health_survey_false"
                                        class="form-check-input"
                                        :value="false"
                                        :disabled="isOrganization">
                                    <label
                                        class="form-check-label"
                                        for="is_health_survey_false">
                                        利用しない
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col">
                        <div class="form-group m-form__group">
                            <label>回答時間（日本時間）</label>
                            <div class="d-flex align-items-center">
                                <b-form-timepicker
                                    v-model="group.health_survey_start_time"
                                    locale="ja"
                                    placeholder="--:--"
                                    minutes-step="30"
                                    :class="{ 'is-invalid' : errors.health_survey_start_time }"
                                    :disabled="isDisabledHealthSurvey">
                                </b-form-timepicker>
                                <div class="pl-2 pr-2 text-muted">〜</div>
                                <b-form-timepicker
                                    v-model="group.health_survey_end_time"
                                    locale="ja"
                                    placeholder="--:--"
                                    minutes-step="30"
                                    :class="{ 'is-invalid' : errors.health_survey_end_time }"
                                    :disabled="isDisabledHealthSurvey">
                                </b-form-timepicker>
                            </div>
                            <div
                                v-if="errors.health_survey_start_time"
                                class="invalid-feedback d-block mt-1">
                                {{ errors.health_survey_start_time[0] }}
                            </div>
                            <div
                                v-else-if="errors.health_survey_end_time"
                                class="invalid-feedback d-block mt-1">
                                {{ errors.health_survey_end_time[0] }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col">
                        <div class="form-group m-form__group checkboxWrapper">
                            <label>通知設定</label>
                            <div>
                                <div class="form-check form-check-inline">
                                    <input
                                        v-model="group.health_survey_day_of_weeks"
                                        type="checkbox"
                                        id="health_survey_day_of_weeks_mon"
                                        class="form-check-input"
                                        value="1"
                                        :disabled="isDisabledHealthSurvey">
                                    <label
                                        class="form-check-label pr-3"
                                        for="health_survey_day_of_weeks_mon">
                                        月
                                    </label>
                                    <input
                                        v-model="group.health_survey_day_of_weeks"
                                        type="checkbox"
                                        id="health_survey_day_of_weeks_tue"
                                        class="form-check-input"
                                        value="2"
                                        :disabled="isDisabledHealthSurvey">
                                    <label
                                        class="form-check-label pr-3"
                                        for="health_survey_day_of_weeks_tue">
                                        火
                                    </label>
                                    <input
                                        v-model="group.health_survey_day_of_weeks"
                                        type="checkbox"
                                        id="health_survey_day_of_weeks_wed"
                                        class="form-check-input"
                                        value="3"
                                        :disabled="isDisabledHealthSurvey">
                                    <label
                                        class="form-check-label pr-3"
                                        for="health_survey_day_of_weeks_wed">
                                        水
                                    </label>
                                    <input
                                        v-model="group.health_survey_day_of_weeks"
                                        type="checkbox"
                                        id="health_survey_day_of_weeks_thu"
                                        class="form-check-input"
                                        value="4"
                                        :disabled="isDisabledHealthSurvey">
                                    <label
                                        class="form-check-label pr-3"
                                        for="health_survey_day_of_weeks_thu">
                                        木
                                    </label>
                                    <input
                                        v-model="group.health_survey_day_of_weeks"
                                        type="checkbox"
                                        id="health_survey_day_of_weeks_fri"
                                        class="form-check-input"
                                        value="5"
                                        :disabled="isDisabledHealthSurvey">
                                    <label
                                        class="form-check-label pr-3"
                                        for="health_survey_day_of_weeks_fri">
                                        金
                                    </label>
                                    <input
                                        v-model="group.health_survey_day_of_weeks"
                                        type="checkbox"
                                        id="health_survey_day_of_weeks_sat"
                                        class="form-check-input"
                                        value="6"
                                        :disabled="isDisabledHealthSurvey">
                                    <label
                                        class="form-check-label pr-3"
                                        for="health_survey_day_of_weeks_sat">
                                        土
                                    </label>
                                    <input
                                        v-model="group.health_survey_day_of_weeks"
                                        type="checkbox"
                                        id="health_survey_day_of_weeks_sun"
                                        class="form-check-input"
                                        value="0"
                                        :disabled="isDisabledHealthSurvey">
                                    <label
                                        class="form-check-label pr-3"
                                        for="health_survey_day_of_weeks_sun">
                                        日
                                    </label>
                                </div>
                            </div>
                            <div
                                v-if="errors.health_survey_day_of_weeks"
                                class="invalid-feedback d-block mt-1">
                                {{ errors.health_survey_day_of_weeks[0] }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>

        <div
            slot="modal-footer"
            class="w-100" :class="{ 'u-loader u-loader--sm' : inProgress }">
            <button
                class="btn btn-yokumiru-orange float-right"
                :disabled="inProgress || telObj.valid === false"
                @click="handleOk">
                追加
            </button>
            <button
                class="btn btn-yokumiru-gray float-right mr-2"
                :disabled="inProgress"
                @click="close">
                キャンセル
            </button>
        </div>
    </b-modal>
</template>

<script>
import constants from '../../../../constants'
import YokumiruHelper from '../../../../YokumiruHelper'
import VueTagsInput from '@johmun/vue-tags-input'

import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

import { quillEditor } from 'vue-quill-editor'

export default {
    components: {
        VueTagsInput,
        quillEditor,
    },

    props: {
        groupType: {
            type: Number,
            required: true,
        },
    },

    data: function () {
        return {
            helper: YokumiruHelper,
            inProgress: false,
            modalShow: false,
            plans: [],
            group: null,
            healthSurveyTemplates: [],
            healthSurveyGroupDetails: [],
            groupManagementTag: '',
            groupManagementTags: [],
            groupPlanIds: [null],
            telObj: {},
            errors: {},
            editorOption: {
                modules: {
                    toolbar: [
                        [{ 'header': 1 }, { 'header': 2 }, 'bold', 'italic', 'underline'],
                        [{ 'color': [] }, { 'background': [] }],
                        ['link'],
                    ],
                },
                placeholder: 'ここに内容を入力してください',
            },
        }
    },

    computed: {
        groupTypeName: function () {
            return this.helper.groupTypeName(this.groupType)
        },

        isOrganization: function () {
            return this.groupType === constants.groupType.organization
        },

        isCompany: function () {
            return this.groupType === constants.groupType.company
        },

        isDisabledHealthSurvey: function () {
            return !this.group.is_health_survey || this.isOrganization
        },

        defaultPlan: function () {
            return this.plans.find((plan) => plan.key === constants.plans.payPer)
        },

        defaultPlanId: function () {
            return this.defaultPlan ? this.defaultPlan.id : null
        },

        filteredPlans: function () {
            return this.plans.filter((plan) => plan.id !== this.defaultPlanId)
        },
    },

    created: function () {
        this.fetchPlans()
        this.fetchHealthSurveyTemplates()
        this.initGroup()
        bus.$on('open-modal-admin-add-group', this.open)
    },

    beforeDestroy: function () {
        bus.$off('open-modal-admin-add-group')
    },

    watch: {
        groupPlanIds: function (ids) {
            if (Array.isArray(ids) && !ids.includes(null)) {
                this.groupPlanIds.push(null)
            }
        },

        'group.is_tab_consultation': function (val) {
            if (!val) {
                this.group.is_viewable_authorizer = false
            }
        },
    },

    methods: {
        /**
         * プラン一覧取得
         */
        fetchPlans: function () {
            axios.get(constants.admin.plans.endpoint.base)
                .then(({data}) => {
                    this.plans = data.data
                })
        },

        /**
         * メンタルチェック質問デッキ一覧取得
         */
        fetchHealthSurveyTemplates: function () {
            axios.get(constants.admin.health_survey_templates.endpoint.base)
                .then(({data}) => {
                    this.healthSurveyTemplates = data.data
                })
        },

        /**
         * メンタルチェック設定の初期化
         */
        initHealthSurveyGroupDetails: function () {
            this.healthSurveyGroupDetails = this.healthSurveyTemplates.map((template) => {
                return {
                    group_id: this.group.id,
                    health_survey_template_id: template.id,
                    is_active: false,
                }
            })
        },

        /**
         * グループの初期化
         */
        initGroup: function () {
            this.groupManagementTags = []
            this.groupPlanIds = [this.defaultPlanId]

            this.group = {
                group_type: this.groupType,
                name: null,
                name_kana: null,
                address: null,
                tel: '',
                representative: null,
                representative_kana: null,
                is_health_package: false,
                is_viewable_authorizer: false,
                is_tab_consultation: false,
                memo: null,
                invitation_message: null,
                group_management_tags: [],
                is_health_survey: false,
                health_survey_start_time: null,
                health_survey_end_time: null,
                health_survey_day_of_weeks: [],
            }
        },

        /**
         * グループ管理用タグ追加時の同期処理
         * @param {array} tags
         */
        syncGroupManagementTag: function (tags) {
            this.groupManagementTags = tags.map((lbl) => {
                return {
                    text: lbl.text,
                    style: 'color: #3461b4; background-color: #f2f4f8; border: 1px solid #dce4ed;',
                    tiClasses: ['ti-valid'], // VueTagsInputが自動生成している
                }
            })
        },

        /**
         * 利用プランの選択プルダウンを追加
         */
        onClickAddSelectPlan: function () {
            this.groupPlanIds.push(null)
        },

        /**
         * 利用プランの選択プルダウンを削除
         * @param {number} index
         */
        onClickRemoveSelectPlan: function (index) {
            this.groupPlanIds.splice(index, 1)
        },

        /**
         * 利用プランの選択状態をアップデート
         */
        updateGroupPlanIdsList: function () {
            // 利用プラン以外を取り除くためにインデックスを取得
            const removeIndexes = this.groupPlanIds.map((id, index) => {
                if (id === null) return null

                const plan = this.plans.find((plan) => plan.id === id)
                if (this.groupPlanIds.includes(plan.id)) {
                    return null
                }
                return index
            }).filter((v) => v !== null).sort().reverse()

            if (removeIndexes.length > 0) {
                removeIndexes.forEach((index) => this.groupPlanIds.splice(index, 1))
            }

            // すべて取り除かれた場合
            if (this.groupPlanIds.length < 1) {
                this.groupPlanIds.push(this.defaultPlanId)
            }
        },

        /**
         * モーダル表示
         */
        open: function () {
            this.initGroup()
            this.initHealthSurveyGroupDetails()
            this.modalShow = true
        },

        /**
         * モーダルを閉じる
         */
        close: function () {
            this.modalShow = false
            this.errors = {}
        },

        /**
         * 追加ボタン押下時
         *
         * @param {object} e
         */
        handleOk: function (e) {
            e.preventDefault()
            this.addGroup()
        },

        /**
         * グループ追加
         */
        addGroup: async function () {
            if (this.inProgress) return
            this.inProgress = true

            this.group.tel = this.telObj.number.e164 || ''
            this.group.tel_national = this.telObj.number.national
            this.group.tel_region = this.telObj.regionCode

            this.group.plan_ids = this.groupPlanIds.filter((id) => id !== null)

            this.group.group_management_tags = []
            this.groupManagementTags.forEach((tag) => {
                this.group.group_management_tags.push(tag.text)
            })

            const endpoint = constants.admin.groups.endpoint.base
            const params = {...this.group}

            if (!params.is_health_survey) {
                params.health_survey_start_time = null
                params.health_survey_end_time = null
                params.health_survey_day_of_weeks = null
            }

            await axios.post(endpoint, params)
                .then(() => {
                    bus.$emit('alert-success', `${this.groupTypeName}を追加しました。`)
                    bus.$emit('fetch-groups')
                    this.errors = {}
                    this.close()
                })
                .catch(({response}) => {
                    if (response.hasOwnProperty('data') && response.data.hasOwnProperty('errors')) {
                        bus.$emit('alert-danger')
                        this.errors = response.data.errors
                    }
                    else {
                        bus.$emit('alert-danger', 'システムエラーが発生しました。')
                    }
                })

            this.inProgress = false
        },

        onValidateTel: function (object) {
            if (object.number.input) {
                this.telObj = object
            }
            else {
                this.telObj = {
                    number: { e164: null, national: null },
                    valid: true,
                    regionCode: null,
                }
            }
        },
    },
}
</script>

<style scoped lang="scss">
.incDecButton {
    width: 37px;
    height: 37px;
    margin-left: 10px;
    line-height: 37px;
    color: #fff;
    font-weight: bold;
    text-align: center;
    background: #4E7DAB;
    border-radius: 5px;
    cursor: pointer;

    &.incDecButtonRemove {
        color: #818181;
        background: #e8e8e8;
    }
}

.modalSection {
    margin-bottom: 15px;
}

.modalHeading {
    color: #333;
    font-size: 16px;
    font-weight: bold;

    &::before {
        content: "";
        position: relative;
        top: 2px;
        display: inline-block;
        width: 4px;
        height: 16px;
        margin-right: 4px;
        background: #3DB073;
    }
}
</style>
